import { useState } from 'react';
import { useNavigate } from 'react-router';
import { useTranslation } from '@/lib/i18n';
import { friendlyMessage } from '@/lib/errors';
import { Button, PageHeaderSticky, StickyFooter, toast } from '@/components/ui';
import { ProductForm, type ProductFormSubmit } from '../components/ProductForm';
import { useProductHomeSections } from '../hooks/useProductHomeSections';
import { useCreateProductMutation } from '../hooks/useProductMutation';
import { useSyncProductEventsMutation } from '../hooks/useProductEventsMutation';
import { useSyncProductCouponsMutation } from '../hooks/useSyncProductCouponsMutation';
import { useSyncProductEarnPolicyMutation } from '@/features/mileage/hooks/useMileageEarnPoliciesQuery';
import { formToCreateRequest } from '../lib/detailMapper';
import { useSaveProductCollectionMutation } from '../productCollection/useProductCollection';
import { normalizeCollectionConfig } from '../productCollection/types';
import { COLLECTION_MALLS, collectionSaveFailedMessage, type CollectionMall } from '../productCollection/collectionMalls';

const FORM_ID = 'product-create-form';

export const ProductCreatePage = () => {
  const { t } = useTranslation('products');
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const mutation = useCreateProductMutation();
  const syncEvents = useSyncProductEventsMutation();
  const syncCoupons = useSyncProductCouponsMutation();
  const syncEarnPolicy = useSyncProductEarnPolicyMutation();
  // 항목2 커스텀 폼/동의 저장은 상품 생성 후 새 productId 로 몰별(4몰) PUT.
  const saveCollection = useSaveProductCollectionMutation();

  // QA-463 — 메인 신상품 섹션 노출(상품 저장 후 새 productId 로 넣는다).
  const homeSectionsState = useProductHomeSections(null);

  const onSubmit = ({ values, eventLinks, couponLinks, earnPolicyId, collectionByMall, homeSections }: ProductFormSubmit) => {
    setSubmitting(true);
    mutation.mutate(formToCreateRequest(values), {
      onError: () => setSubmitting(false),
      onSuccess: async (detail) => {
        const productId = detail.productId;
        if (eventLinks.length > 0) {
          await syncEvents
            .mutateAsync({
              productId,
              links: eventLinks.map((link) => ({
                eventId: link.eventId,
                entryMode: link.entryMode,
                minOrderAmount: link.minOrderAmount ?? null,
                minQuantity: link.minQuantity ?? null,
              })),
            })
            .catch(() => undefined);
        }
        if (couponLinks.length > 0) {
          await syncCoupons
            .mutateAsync({
              productId,
              couponDefinitionIds: couponLinks.map((link) => link.couponId),
            })
            .catch(() => undefined);
        }
        // QA-570 — 체크한 메인 섹션에만 넣는다(등록이라 뺄 것은 없다).
        const homeSectionsFailed = homeSections ? await homeSectionsState.sync(productId, homeSections) : [];
        if (earnPolicyId) {
          await syncEarnPolicy
            .mutateAsync({ productId, policyId: earnPolicyId })
            .catch(() => undefined);
        }
        // 항목2 — 몰별로 비어있지 않은 세트만 PUT(빈 몰은 BE 호출 생략). 한 몰 실패가
        // 다른 몰/네비게이션을 막지 않도록 per-mall catch.
        const failedMalls: CollectionMall[] = [];
        for (const mall of COLLECTION_MALLS) {
          const config = normalizeCollectionConfig(collectionByMall[mall]);
          if (config.fields.length === 0 && config.consents.length === 0) continue;
          await saveCollection
            .mutateAsync({ productId, mall, config })
            .catch(() => failedMalls.push(mall));
        }
        // QA-565 — 상품은 이미 만들어졌으므로 목록으로 가되, 추가입력·동의 실패는 알린다.
        // QA-490 — 저장 완료 알림.
        if (homeSectionsFailed.length > 0) {
          toast.error(`메인 섹션 노출을 반영하지 못했습니다(${homeSectionsFailed.join(', ')}). 메인 페이지 관리에서 확인해 주세요.`);
        }
        if (failedMalls.length > 0) toast.error(collectionSaveFailedMessage(failedMalls));
        else toast.success(t('products.edit.saved', '저장되었습니다.'));
        navigate('/products');
      },
    });
  };

  return (
    <div className="flex flex-col gap-6 pb-20">
      <PageHeaderSticky
        title={t('products.create.title')}
        breadcrumb={[
          { label: t('products.list.title'), href: '/products' },
          { label: t('products.create.breadcrumbCurrent') },
        ]}
      />

      {mutation.isError && (
        <div
          className="rounded-xl px-4 py-3 text-sm"
          role="alert"
          aria-live="polite"
          style={{
            background: 'var(--status-danger-bg)',
            color: 'var(--status-danger-fg)',
            border: '1px solid var(--divider-strong)',
          }}
        >
          {friendlyMessage(mutation.error, t('products.create.error'))}
        </div>
      )}

      <ProductForm
        id={FORM_ID}
        homeSectionOptions={homeSectionsState.options}
        defaultHomeSections={homeSectionsState.included}
        submitting={submitting || mutation.isPending}
        submitLabelKey="products.actions.create"
        onSubmit={onSubmit}
        onCancel={() => navigate('/products')}
        footerSlot={false}
      />

      <StickyFooter>
        <Button
          type="button"
          variant="outline"
          onClick={() => navigate('/products')}
          disabled={submitting || mutation.isPending}
        >
          {t('products.actions.cancel')}
        </Button>
        <Button
          type="submit"
          form={FORM_ID}
          variant="primary"
          disabled={submitting || mutation.isPending}
        >
          {t('products.actions.create')}
        </Button>
      </StickyFooter>
    </div>
  );
};
